import axios from 'axios';
import axiosRetry from 'axios-retry';
import { apiConfig } from '../config/apiConfig';
import { useAuth } from '../context/AuthContext'; // Adjust import path for AuthContext

// Base API URL
const getApiPath = (endpoint: string) => `${apiConfig.apiBaseUrl}${endpoint}`;

// Create Axios instance with default configuration
const apiInstance = axios.create({
  baseURL: apiConfig.apiBaseUrl,
});

axiosRetry(apiInstance, { retries: 3 });


// Request interceptor to add Authorization header
export const setAxiosAuthInterceptor = (getAuthToken: () => Promise<string | undefined>) => {
  apiInstance.interceptors.request.use(
    async (config) => {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

const handleError = (error: any) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;

    if (status === 401) {
      console.error('Unauthorized access - perhaps login expired?');
    } else if (status === 403) {
      console.error('Forbidden - insufficient permissions.');
    } else if (status === 404) {
      console.error('API endpoint not found.');
    } else if (status === 500) {
      console.error('Error, failed API attempt');
    }

    throw new Error(error.response?.data?.message || `API Error (${status})`);
  } else {
    console.error('Unexpected error:', error);
    throw new Error('Unexpected error occurred');
  }
};


// GET Request
export const getData = async (endpoint: string, params = {}) => {
  try {
    const response = await apiInstance.get(getApiPath(endpoint), { params });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// POST Request
export const postData = async (endpoint: string, data: object) => {
  try {
    const response = await apiInstance.post(getApiPath(endpoint), data);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// PUT Request
export const putData = async (endpoint: string, data: object) => {
  try {
    const response = await apiInstance.put(getApiPath(endpoint), data);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// DELETE Request
export const deleteData = async (endpoint: string, params = {}) => {
  try {
    const response = await apiInstance.delete(getApiPath(endpoint), { params });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};
